<template>
  <div class="admin-page wide">
    <div class="page-hero">
      <div>
        <p class="page-kicker">Admin Dashboard</p>
        <h1 class="page-title">{{ selectedOrganizationLabel }}</h1>
        <p class="page-subtitle">{{ adminScopeStatement }}</p>
      </div>
      <div class="hero-badge">
        <i class="fas fa-building"></i>
        <span>{{ formatNumber(totalOrganizationCount) }} organizations</span>
      </div>
    </div>

    <section class="dashboard-filter-panel dashboard-filter-panel--admin">
      <div v-if="hasOrganizations" class="form-section">
        <label class="field-label" for="admin-org-select">Organization</label>
        <select
          id="admin-org-select"
          class="ui-input"
          v-model="org_selected"
          @change="handleDateChange"
        >
          <option value="ALL">All Organizations</option>
          <option
            v-for="org in dashboard_data.organization"
            :key="org.uuid"
            :value="org.uuid"
          >
            {{ org.name }}
          </option>
        </select>
      </div>

      <div class="form-section">
        <label class="field-label" for="admin-start-date">Start date</label>
        <input id="admin-start-date" type="date" class="ui-input" v-model="startDate" @change="handleDateChange">
      </div>

      <div class="form-section">
        <label class="field-label" for="admin-end-date">End date</label>
        <input id="admin-end-date" type="date" class="ui-input" v-model="endDate" :min="startDate" @change="handleDateChange">
      </div>

      <div class="form-section">
        <label class="field-label">Quick range</label>
        <div class="dashboard-filter-presets">
          <button
            type="button"
            class="dashboard-filter-chip"
            :class="{ 'is-active': isLastWeekPreset }"
            @click="setLastWeek"
          >
            Last 7 days
          </button>
          <button
            type="button"
            class="dashboard-filter-chip"
            :class="{ 'is-active': isLastMonthPreset }"
            @click="setLastMonth"
          >
            Last 30 days
          </button>
        </div>
      </div>
    </section>

    <div v-if="requestState.error" class="alert-message">
      <div>
        <i class="fas fa-exclamation-circle"></i>
        <strong>Unable to load dashboard</strong>
        <span>{{ requestState.error }}</span>
      </div>
      <button type="button" class="ghost-chip" @click="getDashboardData">Retry</button>
    </div>

    <div class="metrics-grid">
      <article
        v-for="metric in summaryMetrics"
        :key="metric.key"
        class="metric-card"
        :class="[{ clickable: metric.action }, `metric-card--${metric.variant}`]"
        @click="metric.action && metric.action()"
      >
        <div class="metric-card__icon">
          <i :class="metric.icon"></i>
        </div>
        <div class="metric-card__content">
          <span class="metric-card__label">{{ metric.label }}</span>
          <strong class="metric-card__value">{{ metric.value }}</strong>
          <span class="metric-card__hint">{{ metric.hint }}</span>
        </div>
      </article>
    </div>

    <section v-if="requestState.isLoading" class="state-chart-grid">
      <div v-for="index in 4" :key="index" class="chart-card skeleton-card">
        <div class="skeleton-line skeleton-line--title"></div>
        <div class="skeleton-line skeleton-line--meta"></div>
        <div class="skeleton-chart"></div>
      </div>
    </section>

    <template v-else>
      <section v-if="isDashboardEmpty" class="empty-state-card">
        <div class="empty-state-card__icon">
          <i class="fas fa-chart-area"></i>
        </div>
        <div>
          <h2 class="section-title">No dashboard data in this range</h2>
          <p class="section-description">Expand the date range or switch organizations to surface more activity.</p>
        </div>
      </section>

      <section class="state-chart-grid">
        <article class="chart-card chart-card--span-8">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Patient registrations</h2>
              <p class="section-description">Daily intake across {{ selectedOrganizationLabel.toLowerCase() }} for the selected range.</p>
            </div>
          </div>
          <div class="chart-wrap">
            <Line v-if="hasRegistrationTimelineData" :data="registrationTimelineData" :options="lineChartOptions" />
            <div v-else class="empty-chart">No registration activity in this period.</div>
          </div>
        </article>

        <article class="chart-card chart-card--span-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Case grading</h2>
              <p class="section-description">DR grade mix across screened images.</p>
            </div>
          </div>
          <div class="chart-wrap chart-wrap--compact">
            <Pie v-if="hasCaseGradingData" :data="gradingByCaseData" :options="pieChartOptions" />
            <div v-else class="empty-chart">No case grading data in this period.</div>
          </div>
        </article>

        <article class="chart-card chart-card--span-4">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Patient grading</h2>
              <p class="section-description">Highest-risk grade mix across patients.</p>
            </div>
          </div>
          <div class="chart-wrap chart-wrap--compact">
            <Pie v-if="hasPatientGradingData" :data="gradingByPatientData" :options="pieChartOptions" />
            <div v-else class="empty-chart">No patient grading data in this period.</div>
          </div>
        </article>

        <article class="chart-card chart-card--span-8">
          <div class="section-heading">
            <div>
              <h2 class="section-title">Age group distribution</h2>
              <p class="section-description">Screening outcomes by age band, including Grade 0.</p>
            </div>
          </div>
          <div class="chart-wrap">
            <Bar v-if="hasAgeGroupChartData" :data="ageGroupData" :options="barChartOptions" />
            <div v-else class="empty-chart">No age group distribution available in this period.</div>
          </div>
        </article>
      </section>
    </template>

    <section class="patients-card" id="recent-patients-table">
      <div class="patients-card__header">
        <div class="patients-card__intro">
          <h2 class="section-title">Recent patients</h2>
          <p class="section-description">Sorted by highest grade so urgent cases stay visible first.</p>
        </div>

        <div class="patients-card__controls">
          <div class="segmented-control">
            <button
              type="button"
              :class="{ active: currentFilter === 'all' }"
              @click="setFilter('all')"
            >
              All
            </button>
            <button
              type="button"
              :class="{ active: currentFilter === 'abnormal' }"
              @click="setFilter('abnormal')"
            >
              Abnormal only
            </button>
          </div>

          <label class="rows-control">
            <span>Rows</span>
            <select class="ui-input rows-select" v-model.number="patientPageSize" @change="handlePatientPageSizeChange">
              <option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option>
            </select>
          </label>

          <button type="button" class="secondary-button" @click="$router.push('/patients')">View all</button>
        </div>
      </div>

      <div class="dashboard-table__wrap">
        <table class="patients-table mobile-card-table">
          <thead>
            <tr>
              <th>Patient ID</th>
              <th>Name</th>
              <th>Age</th>
              <th>Age group</th>
              <th>Ethnicity</th>
              <th class="text-center">Highest grade</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="requestState.isLoading">
              <td colspan="6" class="table-empty">Loading recent patients...</td>
            </tr>
            <tr v-else-if="!paginatedPatients.length">
              <td colspan="6" class="table-empty">No patients found for the current filter.</td>
            </tr>
            <tr v-for="patient in paginatedPatients" :key="patient.PatientUuid">
              <td>
                <span class="patient-id">{{ formatPatientIdentifier(patient.PatientIdentifier) }}</span>
              </td>
              <td>
                <span class="patient-name">{{ formatPatientName(patient.PatientName) }}</span>
              </td>
              <td>{{ patient.PatientAgeAtScreening || '—' }}</td>
              <td>
                <span class="pill pill--neutral">{{ patient.age_group || 'Unknown' }}</span>
              </td>
              <td>
                <span class="pill pill--neutral">{{ formatEthnicity(patient.EthnicGroup) }}</span>
              </td>
              <td class="text-center">
                <span class="pill" :class="gradePillClass(patient.highest_grading)">
                  {{ formatGrade(patient.highest_grading) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="patients-card__footer" v-if="filteredPatients.length">
        <span class="patients-card__meta">{{ patientRangeLabel }}</span>

        <div class="pagination-bar">
          <button type="button" class="pager-button" :disabled="patientPage === 1" @click="goToPatientPage(patientPage - 1)">
            Previous
          </button>

          <div class="page-numbers">
            <button
              v-for="page in visiblePatientPages"
              :key="page"
              type="button"
              class="page-number"
              :class="{ active: page === patientPage }"
              @click="goToPatientPage(page)"
            >
              {{ page }}
            </button>
          </div>

          <button
            type="button"
            class="pager-button"
            :disabled="patientPage === patientPageCount"
            @click="goToPatientPage(patientPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement,
} from 'chart.js';
import { Pie, Line, Bar } from 'vue-chartjs';
import { getDashboardbyDate } from '@/services/dashboardServices';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement
);

const EMPTY_DASHBOARD_DATA = {
  case_statistics: {
    total_cases: 0,
    total_abnormal_cases: 0,
    total_abnormal_patients: 0,
    average_image_quality: null,
    grading_counts_by_case: { Grade_2: 0, Grade_1: 0, Grade_0: 0, normal: 0 },
    grading_counts_by_patient: { Grade_2: 0, Grade_1: 0, Grade_0: 0, normal: 0 },
  },
  patient_registration_counts: [],
  patient_age_group_counts: {},
  new_patients: [],
  organization: [],
};

export default {
  name: 'Dashboard',
  components: {
    Pie,
    Line,
    Bar,
  },
  data() {
    return {
      dashboard_data: null,
      startDate: '',
      endDate: '',
      org_selected: 'ALL',
      currentFilter: 'all',
      patientPage: 1,
      patientPageSize: 8,
      pageSizeOptions: [8, 12, 20, 50],
      requestState: {
        isLoading: false,
        error: null,
      },
      pieChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
        },
      },
      lineChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: 'index' },
        plugins: {
          legend: { display: false },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#667085', maxRotation: 0, autoSkip: true },
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(23, 32, 51, 0.08)' },
            ticks: { color: '#667085' },
          },
        },
      },
      barChartOptions: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'bottom' },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { color: '#667085' },
          },
          y: {
            beginAtZero: true,
            grid: { color: 'rgba(23, 32, 51, 0.08)' },
            ticks: { color: '#667085' },
          },
        },
      },
    };
  },
  computed: {
    hasOrganizations() {
      return !!this.dashboard_data?.organization?.length;
    },
    isLastWeekPreset() {
      return this.matchesPresetDays(7);
    },
    isLastMonthPreset() {
      return this.matchesPresetDays(30);
    },
    selectedOrganizationLabel() {
      if (this.org_selected === 'ALL') {
        return 'All organizations';
      }

      const match = this.dashboard_data?.organization?.find((org) => org.uuid === this.org_selected);
      return match?.name || 'Selected organization';
    },
    dateRangeLabel() {
      if (!this.startDate || !this.endDate) {
        return 'Date range not selected';
      }

      return `${this.formatDateLabel(this.startDate)} - ${this.formatDateLabel(this.endDate)}`;
    },
    caseStatistics() {
      return this.dashboard_data?.case_statistics || EMPTY_DASHBOARD_DATA.case_statistics;
    },
    abnormalPatientCount() {
      return this.caseStatistics.total_abnormal_patients || 0;
    },
    totalOrganizationCount() {
      return this.dashboard_data?.organization?.length || 0;
    },
    adminScopeStatement() {
      const totalImages = this.formatNumber(this.caseStatistics.total_cases || 0);
      const abnormalRate = this.abnormalImageRateDisplay;
      const grade2 = this.formatNumber(this.caseStatistics.grading_counts_by_case?.Grade_2 || 0);
      const quality = this.averageImageQualityDisplay;

      if (this.org_selected === 'ALL') {
        return `${totalImages} screenings across all organizations for ${this.dateRangeLabel}, with ${abnormalRate} abnormal images, ${grade2} Grade 2 images, and ${quality} average image quality.`;
      }

      return `${totalImages} screenings for this organization during ${this.dateRangeLabel}, with ${abnormalRate} abnormal images, ${grade2} Grade 2 images, and ${quality} average image quality.`;
    },
    summaryMetrics() {
      return [
        {
          key: 'organizations',
          label: 'Organizations',
          value: this.formatNumber(this.totalOrganizationCount),
          hint: this.org_selected === 'ALL' ? 'Available organizations in admin scope' : 'Admin organization directory',
          icon: 'fas fa-building',
          variant: 'neutral',
        },
        {
          key: 'total-images',
          label: 'Total images',
          value: this.formatNumber(this.caseStatistics.total_cases || 0),
          hint: 'Captured across the selected range',
          icon: 'fas fa-image',
          variant: 'primary',
        },
        {
          key: 'abnormal-images',
          label: 'Abnormal images',
          value: this.formatNumber(this.caseStatistics.total_abnormal_cases || 0),
          hint: `${this.abnormalImageRateDisplay} of all images`,
          icon: 'fas fa-triangle-exclamation',
          variant: 'danger',
          action: this.caseStatistics.total_abnormal_cases ? this.showAbnormalBelow : null,
        },
        {
          key: 'grade-2-images',
          label: 'Grade 2 images',
          value: this.formatNumber(this.caseStatistics.grading_counts_by_case?.Grade_2 || 0),
          hint: 'Highest-risk screening outcome',
          icon: 'fas fa-circle-exclamation',
          variant: 'danger',
          action: (this.caseStatistics.grading_counts_by_case?.Grade_2 || 0) ? this.showAbnormalBelow : null,
        },
        {
          key: 'abnormal-rate',
          label: 'Abnormal rate',
          value: this.abnormalImageRateDisplay,
          hint: 'Abnormal images divided by total images',
          icon: 'fas fa-chart-line',
          variant: this.caseStatistics.total_abnormal_cases ? 'warning' : 'success',
        },
        {
          key: 'new-patients',
          label: 'New patients',
          value: this.formatNumber(this.dashboard_data?.new_patients?.length || 0),
          hint: 'Recently registered in the selected period',
          icon: 'fas fa-user-plus',
          variant: 'success',
        },
        {
          key: 'abnormal-patients',
          label: 'Abnormal patients',
          value: this.formatNumber(this.abnormalPatientCount),
          hint: this.abnormalPatientCount ? 'Patients that need closer review' : 'No abnormal patients flagged',
          icon: 'fas fa-heart-circle-exclamation',
          variant: 'warning',
          action: this.abnormalPatientCount ? this.showAbnormalBelow : null,
        },
        {
          key: 'quality',
          label: 'Average quality',
          value: this.averageImageQualityDisplay,
          hint: this.caseStatistics.total_cases ? this.imageQualityLabel : 'No image quality score yet',
          icon: 'fas fa-camera-retro',
          variant: this.imageQualityVariant,
        },
      ];
    },
    imageQualityVariant() {
      const quality = Number(this.caseStatistics.average_image_quality || 0);
      if (!this.caseStatistics.total_cases) {
        return 'neutral';
      }
      if (quality >= 0.9) {
        return 'success';
      }
      if (quality >= 0.6) {
        return 'primary';
      }
      if (quality >= 0.3) {
        return 'warning';
      }
      return 'danger';
    },
    averageImageQualityDisplay() {
      if (!this.caseStatistics.total_cases || this.caseStatistics.average_image_quality === null || this.caseStatistics.average_image_quality === undefined) {
        return '--';
      }
      return this.formatQualityPercent(this.caseStatistics.average_image_quality);
    },
    abnormalImageRateDisplay() {
      const totalCases = this.caseStatistics.total_cases || 0;
      if (!totalCases) {
        return '0.0%';
      }
      return `${(((this.caseStatistics.total_abnormal_cases || 0) / totalCases) * 100).toFixed(1)}%`;
    },
    imageQualityLabel() {
      const quality = Number(this.caseStatistics.average_image_quality || 0);
      if (quality >= 0.9) {
        return 'Very good image capture quality';
      }
      if (quality >= 0.6) {
        return 'Consistently acceptable image quality';
      }
      if (quality >= 0.3) {
        return 'Quality needs improvement';
      }
      return 'Quality is below the desired threshold';
    },
    hasCaseGradingData() {
      const data = this.caseStatistics.grading_counts_by_case || {};
      return (data.Grade_2 || 0) + (data.Grade_1 || 0) + (data.Grade_0 || 0) + (data.normal || 0) > 0;
    },
    hasPatientGradingData() {
      const data = this.caseStatistics.grading_counts_by_patient || {};
      return (data.Grade_2 || 0) + (data.Grade_1 || 0) + (data.Grade_0 || 0) + (data.normal || 0) > 0;
    },
    hasRegistrationTimelineData() {
      return (this.dashboard_data?.patient_registration_counts || []).some((entry) => (entry.count || 0) > 0);
    },
    hasAgeGroupChartData() {
      const groups = this.dashboard_data?.patient_age_group_counts || {};
      return Object.values(groups).some((group) => (
        (group?.normal || 0) + (group?.Grade_0 || 0) + (group?.Grade_1 || 0) + (group?.Grade_2 || 0) > 0
      ));
    },
    isDashboardEmpty() {
      return !this.requestState.isLoading && !this.requestState.error &&
        (this.caseStatistics.total_cases || 0) === 0 &&
        (this.caseStatistics.total_abnormal_cases || 0) === 0 &&
        (this.caseStatistics.total_abnormal_patients || 0) === 0 &&
        (this.dashboard_data?.new_patients?.length || 0) === 0;
    },
    gradingByCaseData() {
      const data = this.caseStatistics.grading_counts_by_case || {};
      return {
        labels: ['Grade 2', 'Grade 1', 'Grade 0', 'Normal'],
        datasets: [
          {
            data: [data.Grade_2 || 0, data.Grade_1 || 0, data.Grade_0 || 0, data.normal || 0],
            backgroundColor: ['#dc362e', '#f59e0b', '#64748b', '#0fbab2'],
            hoverOffset: 6,
          },
        ],
      };
    },
    gradingByPatientData() {
      const data = this.caseStatistics.grading_counts_by_patient || {};
      return {
        labels: ['Grade 2', 'Grade 1', 'Grade 0', 'Normal'],
        datasets: [
          {
            data: [data.Grade_2 || 0, data.Grade_1 || 0, data.Grade_0 || 0, data.normal || 0],
            backgroundColor: ['#dc362e', '#f59e0b', '#64748b', '#0fbab2'],
            hoverOffset: 6,
          },
        ],
      };
    },
    registrationTimelineData() {
      const data = (this.dashboard_data?.patient_registration_counts || []).filter((entry) => {
        const entryDate = new Date(entry.date).toISOString().split('T')[0];
        return !this.endDate || entryDate <= this.endDate;
      });

      return {
        labels: data.map((entry) => this.formatDateLabel(entry.date)),
        datasets: [
          {
            label: 'Registrations',
            data: data.map((entry) => entry.count || 0),
            borderColor: '#0fbab2',
            backgroundColor: 'rgba(15, 186, 178, 0.12)',
            pointBackgroundColor: '#0fbab2',
            pointBorderWidth: 0,
            pointRadius: 3,
            tension: 0.35,
            fill: true,
          },
        ],
      };
    },
    ageGroupData() {
      const groups = this.dashboard_data?.patient_age_group_counts || {};
      const labels = Object.keys(groups);
      return {
        labels,
        datasets: [
          {
            label: 'Normal',
            data: labels.map((label) => groups[label].normal || 0),
            backgroundColor: '#0fbab2',
            borderRadius: 8,
          },
          {
            label: 'Grade 0',
            data: labels.map((label) => groups[label].Grade_0 || 0),
            backgroundColor: '#64748b',
            borderRadius: 8,
          },
          {
            label: 'Grade 1',
            data: labels.map((label) => groups[label].Grade_1 || 0),
            backgroundColor: '#f59e0b',
            borderRadius: 8,
          },
          {
            label: 'Grade 2',
            data: labels.map((label) => groups[label].Grade_2 || 0),
            backgroundColor: '#dc362e',
            borderRadius: 8,
          },
        ],
      };
    },
    filteredPatients() {
      const patients = [...(this.dashboard_data?.new_patients || [])];
      const gradeOrder = { Grade_2: 3, Grade_1: 2, Grade_0: 1, Normal: 0 };

      patients.sort((left, right) => {
        const gradeDelta = (gradeOrder[right.highest_grading] || 0) - (gradeOrder[left.highest_grading] || 0);
        if (gradeDelta !== 0) {
          return gradeDelta;
        }

        return (left.PatientName || '').localeCompare(right.PatientName || '');
      });

      if (this.currentFilter === 'abnormal') {
        return patients.filter((patient) => ['Grade_0', 'Grade_1', 'Grade_2'].includes(patient.highest_grading));
      }

      return patients;
    },
    patientPageCount() {
      return Math.max(1, Math.ceil(this.filteredPatients.length / this.patientPageSize));
    },
    paginatedPatients() {
      const currentPage = Math.min(this.patientPage, this.patientPageCount);
      const startIndex = (currentPage - 1) * this.patientPageSize;
      return this.filteredPatients.slice(startIndex, startIndex + this.patientPageSize);
    },
    patientRangeLabel() {
      if (!this.filteredPatients.length) {
        return '0 patients';
      }

      const currentPage = Math.min(this.patientPage, this.patientPageCount);
      const start = (currentPage - 1) * this.patientPageSize + 1;
      const end = Math.min(currentPage * this.patientPageSize, this.filteredPatients.length);
      return `Showing ${start}-${end} of ${this.filteredPatients.length} patients`;
    },
    visiblePatientPages() {
      const total = this.patientPageCount;
      const current = Math.min(this.patientPage, total);
      const start = Math.max(1, current - 2);
      const end = Math.min(total, start + 4);
      const pages = [];

      for (let page = Math.max(1, end - 4); page <= end; page += 1) {
        pages.push(page);
      }

      return pages;
    },
  },
  mounted() {
    this.setLastMonth();
  },
  methods: {
    matchesPresetDays(days) {
      if (!this.startDate || !this.endDate) {
        return false;
      }
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - days);
      return this.endDate === end.toISOString().split('T')[0]
        && this.startDate === start.toISOString().split('T')[0];
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString();
    },
    formatQualityPercent(value) {
      if (value === null || value === undefined) {
        return '--';
      }
      return `${(Number(value) * 100).toFixed(0)}%`;
    },
    formatDateLabel(value) {
      if (!value) {
        return '--';
      }

      return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
    formatPatientIdentifier(value) {
      return value ? String(value).toUpperCase() : '—';
    },
    formatPatientName(value) {
      if (!value) {
        return 'Unknown';
      }

      return String(value)
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },
    formatEthnicity(value) {
      if (!value) {
        return 'Unknown';
      }

      return String(value)
        .toLowerCase()
        .split(/[\s_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },
    formatGrade(value) {
      if (!value) {
        return 'Pending';
      }

      return String(value).replace('_', ' ');
    },
    buildRequestEndDate() {
      const end = new Date(this.endDate);
      end.setDate(end.getDate() + 1);
      return end.toISOString().split('T')[0];
    },
    gradePillClass(value) {
      if (value === 'Grade_2') {
        return 'pill--danger';
      }
      if (value === 'Grade_1') {
        return 'pill--warning';
      }
      if (value === 'Grade_0') {
        return 'pill--neutral';
      }
      if (value === 'Normal') {
        return 'pill--success';
      }
      return 'pill--neutral';
    },
    handleDateChange() {
      if (this.startDate && this.endDate) {
        this.getDashboardData();
      }
    },
    async getDashboardData() {
      try {
        this.requestState.isLoading = true;
        this.requestState.error = null;
        this.patientPage = 1;
        this.dashboard_data = await getDashboardbyDate(this.startDate, this.buildRequestEndDate(), this.org_selected);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status === 401) {
          this.requestState.error = 'Your session has expired. Please sign in again.';
          setTimeout(() => {
            this.$router.push('/login');
          }, 1200);
        } else if (!navigator.onLine) {
          this.requestState.error = 'You appear to be offline. Reconnect and try again.';
        } else {
          this.requestState.error = 'The dashboard could not be loaded right now. Please try again.';
        }
        this.dashboard_data = { ...EMPTY_DASHBOARD_DATA };
      } finally {
        this.requestState.isLoading = false;
      }
    },
    setLastWeek() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);
      this.endDate = end.toISOString().split('T')[0];
      this.startDate = start.toISOString().split('T')[0];
      this.handleDateChange();
    },
    setLastMonth() {
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      this.endDate = end.toISOString().split('T')[0];
      this.startDate = start.toISOString().split('T')[0];
      this.handleDateChange();
    },
    setFilter(filter) {
      this.currentFilter = filter;
      this.patientPage = 1;
    },
    handlePatientPageSizeChange() {
      this.patientPage = 1;
    },
    goToPatientPage(page) {
      this.patientPage = Math.min(Math.max(page, 1), this.patientPageCount);
    },
    showAbnormalBelow() {
      this.setFilter('abnormal');
      const table = document.getElementById('recent-patients-table');
      if (table) {
        table.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
  },
};
</script>

<style scoped>
.ghost-chip,
.secondary-button,
.pager-button,
.page-number {
  min-height: 38px;
  border-radius: 10px;
  font-weight: 650;
  transition: all 0.18s ease;
  cursor: pointer;
}

.ghost-chip,
.secondary-button,
.pager-button,
.page-number {
  border: 1px solid var(--ui-border);
  background: #ffffff;
  color: #475569;
  padding: 0 14px;
}

.secondary-button:hover,
.ghost-chip:hover,
.pager-button:hover,
.page-number:hover {
  border-color: rgba(15, 186, 178, 0.4);
  color: #0b7f79;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 42px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(15, 186, 178, 0.12);
  color: #0b7f79;
  font-weight: 700;
  white-space: nowrap;
}

.alert-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.scope-insight-card,
.chart-card,
.empty-state-card,
.patients-card {
  padding: 20px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.scope-insight-card,
.empty-state-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 18px;
}

.scope-insight-card__icon,
.empty-state-card__icon {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  border-radius: 14px;
  background: rgba(15, 186, 178, 0.12);
  color: #0fbab2;
  font-size: 1.1rem;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.metric-card {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 18px;
  border: 1px solid var(--ui-border);
  border-radius: var(--ui-radius);
  background: #ffffff;
  box-shadow: var(--ui-shadow);
}

.metric-card.clickable {
  cursor: pointer;
}

.metric-card__icon {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 12px;
  font-size: 1rem;
}

.metric-card__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.metric-card__label {
  color: var(--ui-muted);
  font-size: 0.78rem;
  font-weight: 650;
}

.metric-card__value {
  color: var(--ui-text);
  font-size: 1.55rem;
  line-height: 1;
  font-weight: 700;
}

.metric-card__hint {
  color: var(--ui-muted);
  font-size: 0.8rem;
  line-height: 1.45;
}

.metric-card--primary .metric-card__icon {
  background: rgba(15, 186, 178, 0.12);
  color: #0fbab2;
}

.metric-card--success .metric-card__icon {
  background: rgba(35, 189, 115, 0.12);
  color: #1d8c58;
}

.metric-card--warning .metric-card__icon {
  background: rgba(253, 185, 19, 0.15);
  color: #8a6500;
}

.metric-card--danger .metric-card__icon {
  background: rgba(220, 54, 46, 0.12);
  color: #dc362e;
}

.metric-card--neutral .metric-card__icon {
  background: rgba(23, 32, 51, 0.08);
  color: #42506a;
}

.state-chart-grid {
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.chart-card {
  grid-column: span 6;
  padding: 16px;
}

.chart-card--wide {
  grid-column: 1 / -1;
}

.chart-card--span-8 {
  grid-column: span 8;
}

.chart-card--span-4 {
  grid-column: span 4;
}

.chart-wrap {
  min-height: 280px;
  position: relative;
}

.chart-wrap--compact {
  min-height: 230px;
}

.empty-chart {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed var(--ui-border);
  border-radius: var(--ui-radius);
  color: var(--ui-muted);
  text-align: center;
  padding: 16px;
}

.patients-card {
  padding: 0;
  overflow: hidden;
}

.patients-card__header,
.patients-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
}

.patients-card__header {
  border-bottom: 1px solid var(--ui-border);
  background: #fbfcfe;
}

.patients-card__intro {
  min-width: 0;
}

.patients-card__controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.patients-card__footer {
  border-top: 1px solid var(--ui-border);
  background: #fbfcfe;
}

.patients-card__meta {
  color: var(--ui-muted);
  font-size: 0.82rem;
}

.segmented-control {
  padding: 3px;
  border-radius: 10px;
  background: #eef2f7;
  display: inline-flex;
  gap: 2px;
}

.segmented-control button {
  min-height: 34px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  padding: 0 12px;
  font-size: 0.8rem;
  font-weight: 650;
  cursor: pointer;
}

.segmented-control button.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}

.rows-control {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
}

.rows-select {
  min-width: 68px;
  min-height: 34px;
  padding: 0 10px;
}

.dashboard-table__wrap {
  overflow-x: auto;
}

.patients-table {
  width: 100%;
  border-collapse: collapse;
}

.patients-table th,
.patients-table td {
  padding: 12px 20px;
  text-align: left;
  white-space: nowrap;
  vertical-align: middle;
}

.patients-table th {
  background: #ffffff;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid var(--ui-border);
}

.patients-table td {
  color: #1e293b;
  font-size: 0.9rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}

.patients-table tbody tr:last-child td {
  border-bottom: 0;
}

.patients-table tbody tr:hover {
  background: #f8fafc;
}

.patient-id {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.84rem;
  font-weight: 650;
  color: #334155;
}

.patient-name {
  font-weight: 600;
  color: #0f172a;
}

.table-empty {
  text-align: center;
  color: var(--ui-muted);
  padding: 28px 20px !important;
}

.text-center {
  text-align: center !important;
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
}

.pill--neutral {
  background: rgba(15, 23, 42, 0.06);
  color: #475569;
}

.pill--success {
  background: rgba(35, 189, 115, 0.14);
  color: #1d8c58;
}

.pill--warning {
  background: rgba(253, 185, 19, 0.16);
  color: #8a6500;
}

.pill--danger {
  background: rgba(220, 54, 46, 0.12);
  color: #b42318;
}

.pagination-bar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-numbers {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

.page-number.active {
  background: rgba(15, 186, 178, 0.12);
  border-color: rgba(15, 186, 178, 0.28);
  color: #0b7f79;
}

.pager-button:disabled,
.page-number:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.skeleton-line,
.skeleton-chart {
  background: linear-gradient(90deg, #eef1f6 25%, #f7f9fc 50%, #eef1f6 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  border-radius: 12px;
}

.skeleton-line--title {
  width: 42%;
  height: 18px;
  margin-bottom: 12px;
}

.skeleton-line--meta {
  width: 26%;
  height: 12px;
  margin-bottom: 18px;
}

.skeleton-chart {
  min-height: 220px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (max-width: 1199px) {
  .metrics-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 960px) {
  .metrics-grid,
  .state-chart-grid {
    grid-template-columns: 1fr;
  }

  .chart-card,
  .chart-card--wide,
  .chart-card--span-8,
  .chart-card--span-4 {
    grid-column: 1 / -1;
  }

  .hero-badge {
    align-self: flex-start;
  }

  .chart-wrap,
  .chart-wrap--compact {
    min-height: 220px;
  }

  .empty-chart {
    min-height: 180px;
  }

  .metric-card {
    padding: 14px;
  }

  .metric-card__value {
    font-size: 1.35rem;
  }

  .patients-card__header,
  .patients-card__footer,
  .alert-message {
    flex-direction: column;
    align-items: stretch;
  }

  .patients-card__header,
  .patients-card__footer {
    padding: 14px;
  }

  .patients-card__controls,
  .pagination-bar,
  .segmented-control,
  .secondary-button,
  .pager-button {
    width: 100%;
  }

  .patients-card__controls {
    justify-content: stretch;
  }

  .segmented-control button {
    flex: 1;
  }

  .patients-table th,
  .patients-table td {
    padding: 10px 12px;
  }
}
</style>
